/**
 * ATENCIÓN CLAVE DE CONSTRUCCIÓN - LABORATORY (CLON WHATSAPP WEB)
 * REGLA BLINDADA: NO MODIFICAR EL CÓDIGO SUBYACENTE NI EL CSS SIN AUTORIZACIÓN O SIN VERIFICAR 2 VECES.
 * Este archivo ha sido estrictamente refactorizado para igualar visualmente a WhatsApp, administrar
 * grabaciones de audio nativas, y conectar con el LabController del Backend pasando todo el historial.
 * 
 * Ver /rules/ConfiAgentes.md antes de alterar rutinas de estado o mediaRecorder.
 */
import React, { useRef, useEffect, useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField
} from "@material-ui/core";
import {
  Add as AddIcon,
  InsertEmoticon as InsertEmoticonIcon,
  Mic,
  Send,
  Stop,
  MoreVert as MoreVertIcon,
  AttachFile,
  Close as CloseIcon,
  Save
} from "@material-ui/icons";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 64px)",
    backgroundColor: "#efeae2",
    backgroundImage: `url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")`,
    backgroundRepeat: "repeat",
    backgroundSize: "400px",
    fontFamily: '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  header: {
    backgroundColor: "#f0f2f5",
    padding: theme.spacing(1, 2),
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #d1d7db",
    zIndex: 10,
    minHeight: "60px"
  },
  headerInfo: {
    marginLeft: theme.spacing(2),
    flexGrow: 1,
  },
  agentName: {
    color: "#111b21",
    fontSize: "16px",
    lineHeight: "21px",
    fontWeight: 500
  },
  agentStatus: {
    color: "#667781",
    fontSize: "13px",
    lineHeight: "21px"
  },
  chatArea: {
    flexGrow: 1,
    padding: theme.spacing(4, 8),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  messageRow: {
    display: "flex",
    width: "100%",
    marginBottom: theme.spacing(1)
  },
  messageRowUser: {
    justifyContent: "flex-end",
  },
  messageRowAgent: {
    justifyContent: "flex-start",
  },
  bubble: {
    maxWidth: "65%",
    padding: "6px 7px 8px 9px",
    borderRadius: 8,
    position: "relative",
    boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
    fontSize: "14.2px",
    lineHeight: "19px",
    color: "#111b21",
    wordWrap: "break-word"
  },
  bubbleUser: {
    backgroundColor: "#d9fdd3",
    borderTopRightRadius: 0,
  },
  bubbleAgent: {
    backgroundColor: "#ffffff",
    borderTopLeftRadius: 0,
  },
  timestamp: {
    fontSize: "11px",
    color: "#667781",
    float: "right",
    marginTop: "4px",
    marginLeft: "12px"
  },
  footer: {
    backgroundColor: "#f0f2f5",
    padding: theme.spacing(1, 2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: "62px"
  },
  inputContainer: {
    flexGrow: 1,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    padding: "9px 12px",
    display: "flex",
    alignItems: "center"
  },
  inputBase: {
    width: "100%",
    fontSize: "15px",
    color: "#111b21 !important",
    "& textarea": {
      color: "#111b21 !important"
    },
    "& input": {
      color: "#111b21 !important"
    }
  },
  iconButton: {
    padding: 8,
    color: "#54656f"
  },
  previewBox: {
    backgroundColor: "#f0f2f5",
    padding: theme.spacing(1, 2),
    borderTop: "1px solid #d1d7db",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  typingIndicator: {
    backgroundColor: "#ffffff",
    padding: "8px 12px",
    borderRadius: 15,
    borderTopLeftRadius: 0,
    color: "#667781",
    fontStyle: "italic",
    fontSize: "13px",
    alignSelf: "flex-start",
    marginBottom: theme.spacing(1),
    boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
  }
}));

const LabSettings = () => {
  const classes = useStyles();
  const [labMessages, setLabMessages] = useState([]);
  const [labText, setLabText] = useState("");
  const [labMediaType, setLabMediaType] = useState("");
  const [labMediaUrl, setLabMediaUrl] = useState("");
  const [labPreviewUrl, setLabPreviewUrl] = useState("");
  const [labSending, setLabSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [testNumber, setTestNumber] = useState("");
  const [contactName, setContactName] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [fixedDelayMs, setFixedDelayMs] = useState("");
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [recording, setRecording] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [labMessages, labSending]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [settingsRes, agentsRes] = await Promise.all([
          api.get("/settings"),
          api.get("/agents")
        ]);
        const data = settingsRes.data || [];
        const testSetting = data.find(s => s.key === "AGENT_TEST_NUMBER");
        setTestNumber(testSetting?.value || "573106901489");
        const nameSetting = data.find(s => s.key === "AGENT_TEST_CONTACT_NAME");
        setContactName(nameSetting?.value || "");
        const delaySetting = data.find(s => s.key === "AGENT_RESPONSE_DELAY_MS");
        setFixedDelayMs(delaySetting?.value || "");
        setAgents(agentsRes.data || []);
        if ((agentsRes.data || []).length > 0) {
          const receptionist = (agentsRes.data || []).find(a => a.role === "receptionist");
          if (receptionist) {
            setSelectedAgentId(String(receptionist.id));
          }
        }
      } catch (e) { }
    };
    loadSettings();
  }, []);

  const selectedAgent = useMemo(
    () => agents.find(a => String(a.id) === String(selectedAgentId)) || null,
    [agents, selectedAgentId]
  );

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      const cleanDelay = String(fixedDelayMs || "").trim();
      const cleanNumber = String(testNumber || "").trim();
      const cleanName = String(contactName || "").trim();

      await Promise.all([
        api.put("/settings/AGENT_RESPONSE_DELAY_MS", { value: cleanDelay }),
        api.put("/settings/AGENT_TEST_NUMBER", { value: cleanNumber }),
        api.put("/settings/AGENT_TEST_CONTACT_NAME", { value: cleanName })
      ]);

      toast.success("Configuración guardada correctamente.");
    } catch (e) {
      toastError(e);
    } finally {
      setSavingSettings(false);
      setSettingsOpen(false);
    }
  };

  const clearMemory = async () => {
    try {
      const params = {};
      if (testNumber) Object.assign(params, { number: testNumber });
      await api.get("/agents/fix/reset-memory", { params });
      setLabMessages([]);
      toast.success("Memoria del simulador limpiada correctamente.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleLabUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    let inferredType = "file";
    if (file.type.startsWith("image/")) inferredType = "photo";
    else if (file.type.startsWith("video/")) inferredType = "video";
    else if (file.type.startsWith("audio/")) inferredType = "audio";

    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/agents/upload", fd);
      const backendUrl = getBackendUrl() || "http://localhost:4000";
      const publicUrl = `${backendUrl}/public/${data.filename}`;
      setLabMediaUrl(publicUrl);
      setLabMediaType(inferredType);
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
      // Validar que e.target exista antes de resetear
      if (e && e.target) {
        e.target.value = null;
      }
    }
  };

  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let preferredMime = "audio/webm";
      if (window.MediaRecorder && window.MediaRecorder.isTypeSupported) {
          if (window.MediaRecorder.isTypeSupported("audio/ogg; codecs=opus")) {
              preferredMime = "audio/ogg; codecs=opus";
          } else if (window.MediaRecorder.isTypeSupported("audio/webm; codecs=opus")) {
              preferredMime = "audio/webm; codecs=opus";
          }
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: preferredMime });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: preferredMime });
        const localUrl = URL.createObjectURL(blob);
        setLabPreviewUrl(prev => {
          if (prev) {
            try { URL.revokeObjectURL(prev); } catch (e) { }
          }
          return localUrl;
        });
        const ext = preferredMime.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `lab-note.${ext}`, { type: preferredMime });
        
        // upload logic
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/agents/upload", fd);
          const backendUrl = getBackendUrl() ? getBackendUrl().replace(/\/$/, "") : "http://localhost:4000";
          const publicUrl = `${backendUrl}/public/${data.filename}`;
          setLabMediaUrl(publicUrl);
          setLabMediaType("audio");
          setLabText("");
        } catch (err) {
          toastError(err);
        } finally {
          setUploading(false);
          setRecording(false);
        }
      };
      mediaRecorder.start();
      recorderRef.current = mediaRecorder;
      setRecording(true);
    } catch (err) {
      console.error(err);
      toast.error("Error al acceder al micrófono. Verifica los permisos de tu navegador.");
      setRecording(false);
    }
  };

  const stopRecording = () => {
    if (!recording || !recorderRef.current) return;
    try {
      if (recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      recorderRef.current.stream.getTracks().forEach(t => t.stop());
    } catch (e) {
      setRecording(false);
    }
  };

  const handleLabSend = async () => {
    if (!labText.trim() && !labMediaUrl && !labPreviewUrl) return;

    // Detect if the user pasted a link directly into the text box and there's no media
    let computedMediaUrl = labMediaUrl;
    let computedMediaType = labMediaType;

    if (!labMediaUrl && labText.match(/^(https?:\/\/|www\.)\S+$/i)) {
      computedMediaUrl = labText;
      computedMediaType = "link";
    }

    const history = labMessages.map(m => ({
      role: m.role === 'agent' ? 'model' : 'user',
      parts: [{ text: m.text || "Archivo multimedia" }]
    }));

    const payload = {
      text: labText,
      mediaUrl: computedMediaUrl,
      mediaType: computedMediaType,
      number: testNumber,
      history,
      agentId: selectedAgentId || null
    };

    let userVoiceUrl = "";
    if (computedMediaType === "audio" && computedMediaUrl) {
      const filename = computedMediaUrl.includes("/public/")
        ? computedMediaUrl.split("/public/").pop()
        : (computedMediaUrl.split("/").pop() || "");
      const backendUrl = getBackendUrl().replace(/\/$/, "");
      userVoiceUrl = filename ? `${backendUrl}/public/${filename}` : computedMediaUrl;
    }

    // Add User Message to UI
    const timeNow = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setLabMessages(prev => [
      ...prev,
      { role: 'user', text: labText, voiceUrl: userVoiceUrl, mediaUrl: computedMediaUrl !== labText ? computedMediaUrl : "", mediaType: computedMediaType, time: timeNow }
    ]);

    setLabText("");
    setLabMediaUrl("");
    setLabMediaType("");
    if (labPreviewUrl) {
      try { URL.revokeObjectURL(labPreviewUrl); } catch (e) { }
    }
    setLabPreviewUrl("");
    setLabSending(true);

    try {
      const { data } = await api.post("/lab/simulate", payload);
      const delayMs = data?.delayMs || 0;
      const backendUrl = getBackendUrl().replace(/\/$/, "");
      const voiceUrl = data?.voiceUrl ? `${backendUrl}/public/${data.voiceUrl}` : "";

      if (data.transcript && computedMediaType === "audio") {
        setLabMessages(prev => {
          const newArray = [...prev];
          if (newArray.length > 0) {
            const lastMsg = newArray[newArray.length - 1];
            if (lastMsg && lastMsg.role === 'user') {
              lastMsg.transcription = data.transcript;
            }
          }
          return newArray;
        });
      }

      setTimeout(() => {
        const rcvTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // DETECCIÓN DE RESET (Fix para limpiar historial visual y evitar Zombie Memory)
        if (data.responseText && data.responseText.includes("[V4 BLINDADO] Sistema Reiniciado")) {
             setLabMessages(prev => [...prev, { role: 'agent', text: data.responseText, voiceUrl, time: rcvTime }]);
             setTimeout(() => {
                 setLabMessages([]);
                 toast.success("✨ Memoria visual y base de datos sincronizadas (Limpias)");
             }, 2500);
        } else {
             setLabMessages(prev => [
              ...prev,
              { role: 'agent', text: data.responseText, voiceUrl, time: rcvTime }
            ]);

             // [FASE 6 - ZONA BLINDADA: Renderizar adjunto si el backend lo envía - PROHIBIDO MODIFICAR - Trae AI 2026-03-10]
             if (data.attachmentUrl) {
               const attachTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
               const attachUrl = data.attachmentUrl.startsWith('http')
                 ? data.attachmentUrl
                 : `${getBackendUrl().replace(/\/$/, '')}/public/${data.attachmentUrl}`;
               const ext = attachUrl.split('?')[0].split('.').pop().toLowerCase();
               const attachType = ['jpg','jpeg','png','gif','webp'].includes(ext) ? 'photo'
                                : ['mp4','webm','mov'].includes(ext) ? 'video' : 'file';
               setLabMessages(prev => [
                 ...prev,
                 { role: 'agent', text: '', mediaUrl: attachUrl, mediaType: attachType, attachName: `Documento adjunto`, time: attachTime }
               ]);
             }
             // [FIN ZONA BLINDADA - Adjunto Fase 6]
        }
        setLabSending(false);
      }, delayMs);
    } catch (err) {
      setLabSending(false);
      toastError(err);
    }
  };

  const hasTextOrMedia = labText.trim().length > 0 || labMediaUrl || labPreviewUrl;

  return (
    <div className={classes.root}>
      {/* Header Estilo WA */}
      <div className={classes.header}>
        <Avatar src="https://ui-avatars.com/api/?name=Robot&background=4caf50&color=fff" />
        <div className={classes.headerInfo}>
          <div className={classes.agentName}>
            {selectedAgent ? selectedAgent.name : "Simulador General"}
          </div>
          <div className={classes.agentStatus}>
            {selectedAgent ? `Voz: ${selectedAgent.voiceId || "Defecto"} • Simulación Activa` : "Laboratorio Activo"}
          </div>
        </div>
        <IconButton className={classes.iconButton} onClick={() => setSettingsOpen(true)}>
          <MoreVertIcon />
        </IconButton>
      </div>


      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <DialogTitle>Configuración de Laboratorio</DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <FormControl fullWidth variant="outlined" size="small" margin="dense">
              <InputLabel>Agente en prueba</InputLabel>
              <Select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                label="Agente en prueba"
                native
              >
                <option value="">Todos (Global)</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.role === 'receptionist' ? `Recepcionista - ` : `Especialista - `} {a.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              label="Retardo fijo por palabra (ms)"
              value={fixedDelayMs}
              onChange={(e) => setFixedDelayMs(e.target.value)}
              placeholder="Ej: 700 o 1000"
              InputProps={{ inputProps: { min: 0, step: 10 } }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Número de pruebas (E.164)"
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              placeholder="Ej: 573138311638"
              helperText="Si está en blanco interactúa con todos los números"
            />
          </Box>
          <Box mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Nombre del Contacto (Prueba)"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Ej: Alejandro Perez"
              helperText="Nombre con el que el agente te reconocerá"
            />
          </Box>
          <Box>
            <Button variant="outlined" color="secondary" fullWidth onClick={clearMemory}>
              Limpiar Memoria de Simulación
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} color="default">
            Cancelar
          </Button>
          <Button
            onClick={saveSettings}
            color="primary"
            variant="contained"
            disabled={savingSettings}
          >
            Guardar Configuración
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Area */}
      <div className={classes.chatArea}>
        {labMessages.length === 0 && (
          <Box display="flex" justifyContent="center">
            <Typography variant="caption" style={{ backgroundColor: "#e1f5fe", padding: "5px 12px", borderRadius: 10, color: "#111b21", boxShadow: "0 1px 0.5px rgba(11,20,26,.13)" }}>
              Los mensajes y llamadas están resguardados en este simulador local (WhatsApp Clone).
            </Typography>
          </Box>
        )}

        {labMessages.map((m, idx) => (
          <div key={idx} className={`${classes.messageRow} ${m.role === "user" ? classes.messageRowUser : classes.messageRowAgent}`}>
            <div className={`${classes.bubble} ${m.role === "user" ? classes.bubbleUser : classes.bubbleAgent}`}>

              {/* Media Block Rendering */}
              {m.mediaUrl && m.mediaType === "photo" && (
                <img src={m.mediaUrl} alt="Adjunto" style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 4 }} />
              )}
              {m.mediaUrl && m.mediaType === "video" && (
                <video src={m.mediaUrl} controls style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 4 }} />
              )}
              {/* [FASE 6] Renderizado de archivo PDF/documento enviado por el agente */}
              {m.mediaUrl && m.mediaType === "file" && (
                <a href={m.mediaUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'#f0f2f5', borderRadius:8, textDecoration:'none', color:'#111b21', marginBottom:4 }}>
                  <span style={{ fontSize:24 }}>📄</span>
                  <span style={{ fontSize:13, fontWeight:500 }}>{m.attachName || 'Documento adjunto'}</span>
                </a>
              )}
              {m.voiceUrl && (
                <audio src={m.voiceUrl} controls preload="none" style={{ maxWidth: 220, height: 40, outline: 'none' }} />
              )}

              {m.text && (
                <Typography style={{ fontSize: "14.2px", whiteSpace: "pre-wrap", color: "#111b21" }}>{m.text}</Typography>
              )}

              {m.transcription && (
                <Typography style={{ fontSize: "12px", fontStyle: "italic", marginTop: 4, display: "block", color: "#111b21" }}>
                  Transcipción: {m.transcription}
                </Typography>
              )}

              <span className={classes.timestamp}>{m.time || "10:00"} {m.role === "user" && "✓✓"}</span>
            </div>
          </div>
        ))}

        {labSending && (
          <div className={`${classes.messageRow} ${classes.messageRowAgent}`} style={{ marginTop: 8 }}>
            <div className={classes.typingIndicator}>
              Escribiendo...
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Area pre-envío para adjuntos */}
      {(labMediaUrl || labPreviewUrl) && !recording && (
        <div className={classes.previewBox}>
          <div>
            <Typography variant="body2" style={{ fontWeight: 500, color: "#111b21" }}>
              {labMediaType === "photo" && "📷 Imagen seleccionada"}
              {labMediaType === "video" && "🎥 Video seleccionado"}
              {labMediaType === "audio" && "🎙️ Nota de voz (Archivo)"}
            </Typography>
          </div>
          <IconButton size="small" onClick={() => { setLabMediaUrl(""); setLabPreviewUrl(""); setLabMediaType(""); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      {/* Footer / Input Estilo WA */}
      <div className={classes.footer}>
        <IconButton className={classes.iconButton}>
          <InsertEmoticonIcon />
        </IconButton>
        <input
          type="file"
          hidden
          onChange={handleLabUpload}
          id="wa-attach"
          accept="*/*"
        />
        <label htmlFor="wa-attach" style={{ margin: 0 }}>
          <IconButton component="span" className={classes.iconButton} disabled={uploading || recording}>
            {uploading ? <CircularProgress size={20} /> : <AttachFile style={{ transform: "rotate(45deg)" }} />}
          </IconButton>
        </label>

        <div className={classes.inputContainer}>
          <InputBase
            className={classes.inputBase}
            placeholder={recording ? "Grabando audio... (Presiona detener)" : "Escribe un mensaje"}
            value={labText}
            onChange={(e) => setLabText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (hasTextOrMedia && !uploading && !recording) handleLabSend();
              }
            }}
            multiline
            maxRows={5}
            disabled={recording || uploading}
          />
        </div>

        {hasTextOrMedia && !recording ? (
          <IconButton className={classes.iconButton} onClick={handleLabSend} disabled={labSending || uploading}>
            {labSending ? <CircularProgress size={20} /> : <Send style={{ color: "#00a884" }} />}
          </IconButton>
        ) : (
          <IconButton
            className={classes.iconButton}
            onClick={recording ? stopRecording : startRecording}
            disabled={labSending || uploading}
          >
            {recording ? <Stop style={{ color: "#ea0038" }} /> : <Mic />}
          </IconButton>
        )}
      </div>

    </div>
  );
};

export default LabSettings;
